import {
  Box,
  Button,
  Chip,
  Modal,
  Stack,
  styled,
  Typography,
} from "@mui/material";

import { Icons } from "../../../assets/icon";
import CustomTimePicker from "../../../TimePicker/SlideTimePicker";
import CustomDatePicker from "../../../DatePicker/CustomDatePicker";

import * as Yup from "yup";
import { Form, Formik } from "formik";

import utc from "dayjs/plugin/utc";
import dayjs, { Dayjs } from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import ChipDesign from "../../../Profile/TalentInfo/ChipDesign";
import { motion, number } from "framer-motion";
import { useState } from "react";
import { enqueueSnackbar } from "notistack";
import {
  useDeleteGTPAvailability,
  useEditGtpAvailability,
  useGetGTPAvailability,
} from "../../../../services/api/GTP/gtpSchedule.api";
import useCurrentGTP from "../../../../hooks/useGTP";
import { useCurrentTalentProfile } from "../../../../hooks";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useGetTalentAvailability } from "../../../../services/api/availability.api";
import {
  useDeleteTalentAvailability,
  useEditTalentAvailability,
} from "../../../../services/api/talentSchedule.api";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isToday);
dayjs.extend(utc);

type EditAvailabilityModalProps = {
  onClose?: () => void; // 👈 from scheduleXcalender
  calendarEvent: any;
  open: boolean;
  isGTP: boolean;
};

type DateTimeEntry = {
  start: string;
  end: string;
};

interface FormValues {
  date: Dayjs | null;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
}

export const NewStyledNetflixButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  background: "#E50914", // Netflix Red
  color: "#ffffff",
  padding: "12px 32px",
  textTransform: "none",
  fontWeight: 900,
  letterSpacing: ".3px",
  fontFamily: "'Lexend', sans-serif",
  boxShadow: "0 10px 28px rgba(229, 9, 20, 0.35)",
  transition: "all 0.2s ease-in-out",

  [theme.breakpoints.up("md")]: {
    fontSize: "1rem",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "0.95rem",
  },

  "&:hover": {
    background: "#b20710", // darker Netflix red
    boxShadow: "0 12px 32px rgba(229, 9, 20, 0.45)",
  },
  "&:disabled": {
    background: "rgba(229, 9, 20, 0.35)",
    color: "rgba(255,255,255,0.65)",
    cursor: "not-allowed",
    boxShadow: "none",
  },
}));

const getDefaultValues = (
  initialSlot: {
    id?: number | string;
    start_time?: string;
    end_time?: string;
    day?: string;
    start?: string; // e.g., "2025-06-27 00:45"
    end?: string; // e.g., "2025-06-27 01:45"
  } | null
): FormValues => {
  if (!initialSlot) {
    console.log("initialSlot is null or undefined");
    return {
      date: null,
      startTime: null,
      endTime: null,
    };
  }

  const { id, start, end } = initialSlot;

  const slot: FormValues = {
    date: null,
    startTime: null,
    endTime: null,
  };

  if (start && dayjs(start).isValid()) {
    const [startDate] = start.split(" ");
    slot.date = dayjs(startDate); // Only date
    slot.startTime = dayjs(start); // Full date-time
  }

  if (end && dayjs(end).isValid()) {
    slot.endTime = dayjs(end); // Full date-time
  }

  return slot;
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "left",
  padding: "24px",
  width: { xs: "95%", md: "600px" },
  gap: "var(--spacing-4x)",
  borderRadius: "12px",
  bgcolor: "var(--neutralWhite)",
  boxShadow: "var(--shadow-smooth)",
};

const EditDeleteAvailability = ({
  onClose,
  calendarEvent,
  isGTP,
}: EditAvailabilityModalProps) => {
  const { currentGTP } = useCurrentGTP();
  const { currentProfile } = useCurrentTalentProfile();
  const [editSlot, setEditSlot] = useState<any | null>(null); // Track the slot being edited
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const queryClient = useQueryClient();

  const [overlapError, setOverlapError] = useState("");

  const [formInitialValues, setFormInitialValues] = useState<FormValues>(
    getDefaultValues(calendarEvent)
  );
  console.log(calendarEvent, "calendarEvent");

  const formatDate = (input: string | Date): string => {
    const date = new Date(input);
    return date.toISOString().split("T")[0];
  };

  const groupedByDate: Record<string, DateTimeEntry[]> = {};

  const extractedId = calendarEvent?.id
    ? Number(calendarEvent.id.toString().split("availability-")[1])
    : 0;

  const { data: GTPAvailability } = useGetGTPAvailability({
    options: {
      enabled: isGTP && !!currentGTP?.id,
    },
    params: {
      gtpId: Number(currentGTP?.id),
    },
    key: ["gtp-availability", currentGTP?.id],
  });

  const { data: talentAvailability } = useGetTalentAvailability({
    options: {
      enabled: !!currentProfile?.id,
    },
    params: {
      id: currentProfile?.id,
    },
    key: ["talent-availability", currentProfile?.id],
  });

  const { update: editGTPAvailability } = useEditGtpAvailability({
    key: ["edit-gtp-availability"],
    params: isGTP
      ? {
          id: extractedId,
          gtpId: Number(currentGTP?.id) || 0,
          headers: {
            "talent-id": currentProfile?.id || "0",
          },
        }
      : undefined,
  });

  const { update: editTalentAvailability } = useEditTalentAvailability({
    key: ["edit-talent-availability"],
    params: !isGTP
      ? {
          id: extractedId,
          talentId: Number(currentProfile?.id) || 0,
        }
      : undefined,
  });

  const { mutate: deleteGTPAvailability } = useDeleteGTPAvailability({
    key: ["delete-gtp-availability"],
    params: isGTP
      ? {
          id: extractedId,
          gtpId: Number(currentGTP?.id) || 0,
          headers: {
            "talent-id": currentProfile?.id || "0",
          },
        }
      : undefined,
  });

  const { mutate: deleteTalentAvailability } = useDeleteTalentAvailability({
    key: ["delete-talent-availability"],
    params: !isGTP
      ? {
          id: extractedId,
          talentId: Number(currentProfile?.id) || 0,
        }
      : undefined,
  });

  const availability = isGTP ? GTPAvailability : talentAvailability;
  const editAvailability = isGTP ? editGTPAvailability : editTalentAvailability;
  const deleteAvailability = isGTP
    ? deleteGTPAvailability
    : deleteTalentAvailability;

  const matchedAvailability = availability?.data?.find(
    (a) => a.id === extractedId
  );

  let hasBookedSlots = false;
  const eventEnd = dayjs(calendarEvent.end);
  const eventStart = dayjs(calendarEvent.start);
  if (matchedAvailability) {
    hasBookedSlots = matchedAvailability.booked_slots.length > 0;
    const availabilityStart = dayjs(matchedAvailability.start_date_time);
    const availabilityEnd = dayjs(matchedAvailability.end_date_time);

    const isTimeMatch =
      eventStart.isSame(availabilityStart) && eventEnd.isSame(availabilityEnd);

    console.log("✅ Time Match:", isTimeMatch);
    console.log("📌 Has Booked Slots:", hasBookedSlots);
  } else {
    console.log("❌ No matching availability found.");
  }

  const newValidationSchema = Yup.object().shape({
    date: Yup.mixed<Dayjs>()
      .nullable()
      .required("Date is required")
      .test("is-valid", "Invalid date format", (value) =>
        dayjs(value).isValid()
      )
      .test(
        "not-past",
        "Date cannot be in the past",
        (value) =>
          !dayjs(value).isSameOrBefore(dayjs().subtract(1, "day"), "day")
      ),

    startTime: Yup.mixed<Dayjs>()
      .nullable()
      .required("Start time is required")
      .test("is-valid", "Invalid time format", (value) =>
        dayjs(value).isValid()
      )
      .test(
        "not-past-time",
        "Start time cannot be in the past for today",
        function (value) {
          const { date } = this.parent;
          if (!date) return true;

          return !(
            dayjs(date).isToday() &&
            dayjs(value).isSameOrBefore(dayjs(), "minute")
          );
        }
      ),

    endTime: Yup.mixed<Dayjs>()
      .nullable()
      .required("End time is required")
      .test("is-valid", "Invalid time format", (value) =>
        dayjs(value).isValid()
      )
      .test(
        "after-start",
        "End time must be after start time",
        function (value) {
          const { startTime } = this.parent;
          if (!startTime || !value) return true;
          return dayjs(value).isAfter(dayjs(startTime));
        }
      ),
  });

  const onEdit = async ({
    start_date_time,
    end_date_time,
  }: {
    start_date_time: string;
    end_date_time: string;
  }) => {
    try {
      await editAvailability(
        {
          start_date_time,
          end_date_time,
        },
        {
          onSuccess: () => {
            enqueueSnackbar("Availability edited successfully", {
              variant: "success",
            });

            setEditSlot(null);
            setFormInitialValues({
              date: dayjs(start_date_time),
              startTime: dayjs(start_date_time),
              endTime: dayjs(end_date_time),
            });
            queryClient.invalidateQueries(["gtp-availability", currentGTP?.id]);
            queryClient.invalidateQueries([
              "talent-availability",
              currentProfile?.id,
            ]);

            if (onClose) {
              onClose();
            }
          },
        }
      );
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), { variant: "error" });
    }

    setOverlapError("");
  };

  const handleEdit = (
    { date, startTime, endTime }: FormValues,
    resetForm?: () => void
  ) => {
    if (date?.isValid() && startTime?.isValid() && endTime?.isValid()) {
      const startDateTime = date
        .hour(startTime.hour())
        .minute(startTime.minute())
        .utc()
        .toISOString();

      const endDateTime = date
        .hour(endTime.hour())
        .minute(endTime.minute())
        .utc()
        .toISOString();

      // Convert to Date objects for comparison
      const selectedStart = new Date(startDateTime);
      const selectedEnd = new Date(endDateTime);

      const selectedDate = formatDate(selectedStart); // e.g., "2025-04-17"

      // Check if there's any existing availability for the selected date
      const existingEntries = groupedByDate[selectedDate];

      if (existingEntries) {
        for (const { start, end } of existingEntries) {
          const existingStart = new Date(start);
          const existingEnd = new Date(end);

          if (selectedStart < existingEnd && selectedEnd > existingStart) {
            setOverlapError(
              "The selected time range overlaps with an existing range."
            );

            console.log(overlapError);

            return;
          }
        }
      }

      console.log("Edited successfully!");

      onEdit({
        start_date_time: startDateTime,
        end_date_time: endDateTime,
      });
    }
  };

  const shouldDisableDate = (date: any) => {
    const today = dayjs();
    const twoWeeksFromNow = today.add(14, "day");

    return date.isAfter(twoWeeksFromNow, "day");
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteAvailability(
        {
          id: extractedId,
          gtpId: Number(currentGTP?.id) || 0,
        },
        {
          onSuccess: () => {
            enqueueSnackbar("Availability deleted successfully", {
              variant: "success",
            });
            if (onClose) onClose();
            setConfirmDeleteOpen(false);
            queryClient.invalidateQueries(["gtp-availability", currentGTP?.id]);
            queryClient.invalidateQueries([
              "talent-availability",
              currentProfile?.id,
            ]);
          },
        }
      );
    } catch (err) {
      enqueueSnackbar("Failed to delete availability", {
        variant: "error",
      });
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteOpen(false);
  };

  return (
    <Box>
      <Formik
        initialValues={formInitialValues}
        enableReinitialize
        validationSchema={newValidationSchema}
        onSubmit={(values, { resetForm }) => {
          handleEdit(values, resetForm);
        }}
      >
        {({ values, isValid, isSubmitting }) => {
          const isFormUnchanged =
            values.date?.isSame(formInitialValues.date, "day") &&
            values.startTime?.isSame(formInitialValues.startTime, "minute") &&
            values.endTime?.isSame(formInitialValues.endTime, "minute");
          return (
            <Form>
              <Box sx={style}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: "var(--spacing-4x)",
                  }}
                >
                  <Stack
                    sx={{
                      display: "flex",
                      width: "100%",
                      flexDirection: "column",
                      gap: "var(--spacing-4x)",
                    }}
                    gap={"var(--spacing-5x)"}
                  >
                    <Box
                      sx={{
                        borderRadius: "28px",
                        border: "8px solid var(--primary-100)",
                        background: "var(--primary-200)",
                        display: "flex",
                        padding: "var(--spacing-3x)",
                        alignItems: "center",
                        width: "fit-content",
                      }}
                    >
                      <Icons.NewCalendar fill="var(--primary-600)" />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "var(--neutral-1000)",
                          font: "var(--semibold-sm)",
                        }}
                      >
                        Edit/Delete Availability
                      </Typography>
                      <Typography
                        sx={{
                          color: "var(--neutral-400)",
                          font: { xs: "var(--body-xs)", md: "var(--body-sm)" },
                        }}
                      >
                        Edit or delete the date and time according to your
                        availability.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={"var(--spacing-3x)"}
                        >
                          <Box
                            component="img"
                            src={
                              currentGTP?.profile_image ??
                              currentProfile?.profile_image ??
                              ""
                            }
                            sx={{
                              objectFit: "cover",
                              display: "flex",
                              width: "40px",
                              height: "40px",
                              justifyContent: "center",
                              alignItems: "center",
                              borderRadius: "7.5px",
                              background: "#80C2FE",
                            }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Stack
                              direction="column"
                              spacing={"var(--spacing-1x)"}
                            >
                              <Typography
                                sx={{
                                  color: "var(--neutralBlack)",
                                  font: {
                                    xs: "var(--semibold-sm)",
                                    md: "var(--semibold-md)",
                                  },
                                }}
                              >
                                {currentGTP?.name ??
                                  currentProfile?.username ??
                                  ""}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ width: "100%" }}>
                        <CustomDatePicker
                          name="date"
                          label="Date"
                          shouldDisableDate={shouldDisableDate}
                          disablePast
                          setOverlapError={setOverlapError}
                        />
                      </Box>

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <CustomTimePicker
                            name="startTime"
                            label="Start Time"
                            minTime={
                              values.date?.isToday() ? dayjs() : undefined
                            }
                            setOverlapError={setOverlapError}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <CustomTimePicker
                            name="endTime"
                            label="End Time"
                            minTime={values.startTime || undefined}
                            setOverlapError={setOverlapError}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Stack>
                  <Typography sx={{ color: "#d32f2f", font: "var(--body-sm)" }}>
                    {overlapError}
                    {hasBookedSlots &&
                      `This slot ${dayjs(matchedAvailability?.start_date_time).isBefore(dayjs()) ? "had" : "has"}  bookings so it can't be edited or deleted`}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "end",
                      gap: "var(--spacing-4x)",
                    }}
                  >
                    <Button
                      onClick={onClose}
                      sx={{
                        borderRadius: "var(--spacing-2x)",
                        border: "1px solid var(--neutral-200) !important",
                        backgroundColor: "var(--neutral-50)",
                        color: "var(--neutral-900)",
                        font: {
                          xs: "var(--semibold-sm)",
                          md: "var(--semibold-md)",
                        },
                        padding: "12px var(--spacing-6x)",
                        textTransform: "none",
                        "&:hover": {
                          boxShadow: "var(--shadow-md)",
                        },
                      }}
                    >
                      Cancel
                    </Button>
                    {!hasBookedSlots && (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleDeleteClick}
                          sx={{
                            borderRadius: "var(--spacing-2x)",
                            border:
                              "1px solid var(--destructive-600) !important",
                            color: "var(--destructive-600)",
                            backgroundColor: "var(--neutralWhite)",
                            font: {
                              xs: "var(--semibold-sm)",
                              md: "var(--semibold-md)",
                            },
                            padding: "12px var(--spacing-6x)",
                            textTransform: "none",
                            boxShadow: "none",
                          }}
                        >
                          Delete
                        </Button>
                        <NewStyledNetflixButton
                          type="submit"
                          disabled={
                            !isValid ||
                            !values.date ||
                            !values.startTime ||
                            !values.endTime ||
                            isSubmitting ||
                            isFormUnchanged
                          }
                        >
                          Edit
                        </NewStyledNetflixButton>
                      </>
                    )}
                    <Dialog
                      open={confirmDeleteOpen}
                      onClose={handleCancelDelete}
                      maxWidth="xs"
                      fullWidth
                    >
                      <DialogTitle
                        sx={{
                          color: "var(--neutral-1000)",
                          font: "var(--semibold-md)",
                        }}
                      >
                        Confirm Deletion
                      </DialogTitle>
                      <DialogContent>
                        <Typography
                          sx={{
                            color: "var(--neutral-1000)",
                            font: "var(--body-sm)",
                          }}
                        >
                          Are you sure you want to delete this availability
                          slot? This action cannot be undone.
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          onClick={handleCancelDelete}
                          sx={{
                            borderRadius: "var(--spacing-2x)",
                            border: "1px solid var(--neutral-200) !important",
                            backgroundColor: "var(--neutral-50)",
                            color: "var(--neutral-900)",
                            font: {
                              xs: "var(--semibold-sm)",
                              md: "var(--semibold-md)",
                            },
                            padding: "12px var(--spacing-6x)",
                            textTransform: "none",
                            "&:hover": {
                              boxShadow: "var(--shadow-md)",
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleConfirmDelete}
                          sx={{
                            borderRadius: "var(--spacing-2x)",
                            background: "var(--button-gradient)",
                            color: "var(--neutral-100)",
                            font: {
                              xs: "var(--semibold-sm)",
                              md: "var(--semibold-md)",
                            },
                            padding: "12px var(--spacing-6x)",
                            textTransform: "none",
                            "&:hover": {
                              background: "var(--buton-gradient-hover)",
                            },
                            "&:disabled": {
                              background: "var(--button-gradient-disabled)",
                              color: "var(--neutral-100)",
                              cursor: "not-allowed",
                            },
                          }}
                          disabled={
                            !isValid ||
                            !values.date ||
                            !values.startTime ||
                            !values.endTime ||
                            isSubmitting
                          }
                        >
                          Confirm Delete
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </Box>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default EditDeleteAvailability;
